import { useState } from 'react';
import { useSnackbar } from 'notistack';
import { updateUserExamBE } from '../services/users';
import { fetchExams } from '../services/exams';

interface UseUserExamUpdateOptions {
  showSuccessMessage?: boolean;
  clearTodaySession?: boolean;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
}

interface UseUserExamUpdateReturn {
  updateExam: (params: UpdateUserExamParams) => Promise<void>;
  isUpdating: boolean;
}

// Type for updateUserExamBE parameters
interface UpdateUserExamParams {
  userId: string;
  examId: string;
  examName?: string | null;
  examDate?: string | null;
  primary?: boolean;
  selectedFlashcardCount?: number;
  selectedVignetteCount?: number;
  isUsingFlashcards?: boolean;
  isUsingQbank?: boolean;
  hasCompletedRealExam?: boolean;
  realExamScore?: number | null;
  ignoredExamDate?: boolean;
  clearTodaySession?: boolean;
  lectureMode?: 'AUTOPILOT' | 'MANUAL';
}

export const useUserExamUpdate = (
  refreshStudySessionAndContent?: () => void,
  options: UseUserExamUpdateOptions = {}
): UseUserExamUpdateReturn => {
  const {
    showSuccessMessage = true,
    clearTodaySession = false,
    onSuccess,
    onError,
  } = options;

  const [isUpdating, setIsUpdating] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const updateExam = async (params: UpdateUserExamParams) => {
    try {
      setIsUpdating(true);

      // Update the exam with provided parameters
      await updateUserExamBE({
        ...params,
        clearTodaySession: clearTodaySession || params.clearTodaySession,
      });

      // We only need to refetch the exams if the primary exam potentially changed
      if (params.primary) {
        await fetchExams();
      }

      if (refreshStudySessionAndContent) {
        await refreshStudySessionAndContent();
      }

      // Show success message if enabled
      if (showSuccessMessage) {
        enqueueSnackbar('Exam updated.', {
          autoHideDuration: 3000,
          style: { width: 'fit-content' },
        });
      }

      // Call success callback if provided
      onSuccess?.();
    } catch (error) {
      console.error('Error updating exam:', error);
      onError?.(error);
    } finally {
      setIsUpdating(false);
    }
  };

  return { updateExam, isUpdating };
};
